// src/components/admin/AdminGroups.tsx
import React, { useState } from 'react';
import { Users, Trash2, Search } from 'lucide-react';
import './AdminGroups.css';
import { GroupData } from './types';
import GroupDetailsModal from '../dashboard/groups/GroupDetailsModal';

interface AdminGroupsProps {
    groups: GroupData[];
    totalGroups: number;
    onDeleteGroup: (groupId: string, groupName: string) => void;
    onShowToast: (message: string, type: 'success' | 'error') => void;
}

const AdminGroups: React.FC<AdminGroupsProps> = ({
    groups,
    totalGroups,
    onDeleteGroup,
    onShowToast
}) => {
    const [search, setSearch] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState<GroupData | null>(null);
    const [selectedGroupIconUrl, setSelectedGroupIconUrl] = useState<string | null>(null);
    const API_BASE_URL = '/api';

    const [members, setMembers] = useState<any[]>([]);
    const [loadingMembers, setLoadingMembers] = useState(false);

    const handleViewDetails = async (group: GroupData, iconUrl: string | null) => {
        setSelectedGroup(group);
        setSelectedGroupIconUrl(iconUrl);
        setModalOpen(true);
        setLoadingMembers(true);
        setMembers([]);

        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/admin/groups/${group._id}/members`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                const adapted = (data.members || []).map((m: any) => ({
                    userId: m._id,
                    username: m.username,
                    profilePhoto: m.profilePhoto,
                    role: m.role || 'member',
                    stats: {
                        cardsAnswered: m.cardsAnswered ?? 0,
                        cardsMastered: m.cardsMastered ?? 0,
                        questionsAnswered: m.questionsAnswered ?? 0,
                        questionsCorrect: m.questionsCorrect ?? 0,
                        studyScore: m.totalCardsStudied ?? m.studyScore ?? 0,
                    },
                    joinedAt: m.joinedAt ?? new Date(),
                }));
                setMembers(adapted);
            } else {
                onShowToast('Erro ao carregar membros do grupo', 'error');
            }
        } catch (error) {
            console.error('Erro ao buscar membros:', error);
            onShowToast('Erro ao carregar membros', 'error');
        } finally {
            setLoadingMembers(false);
        }
    };

    return (
        <div className="adm-grp-container adm-page">
            <div className="adm-page-header" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <h1 className="adm-page-title" style={{ margin: 0 }}>Grupos de Estudo</h1>
                <span className="adm-grp-count">{totalGroups} grupos</span>
            </div>

            <div className="adm-grp-search-wrap">
                <Search size={15} className="adm-grp-search-icon" />
                <input
                    className="adm-grp-search"
                    placeholder="Pesquisar grupo..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
            </div>

            {groups.length === 0 ? (
                <div className="adm-grp-empty">
                    <Users size={48} />
                    <p>Nenhum grupo criado ainda</p>
                </div>
            ) : (
                <div className="adm-grp-grid">
                    {groups.filter(g => g.name.toLowerCase().includes(search.toLowerCase())).map((group) => {
                        const groupIconUrl = group.icon || (group as any).groupIcon || (group as any).iconUrl || null;

                        return (
                            <div key={group._id} className="adm-grp-card">
                                <div className="adm-grp-card-header">
                                    <div
                                        className="adm-grp-card-title-row"
                                        onClick={() => handleViewDetails(group, groupIconUrl)}
                                    >
                                        {groupIconUrl ? (
                                            <img
                                                src={groupIconUrl}
                                                alt={group.name}
                                                className="adm-grp-card-icon adm-grp-card-icon-btn"
                                            />
                                        ) : (
                                            <div className="adm-grp-card-icon-fallback adm-grp-card-icon-btn">
                                                <Users size={18} />
                                            </div>
                                        )}
                                        <h3>{group.name}</h3>
                                    </div>
                                </div>

                                {group.description && (
                                    <p className="adm-grp-description">{group.description}</p>
                                )}

                                <div className="adm-grp-info">
                                    <div className="adm-grp-info-item">
                                        <span className="adm-grp-info-label">CRIADOR</span>
                                        <span className="adm-grp-info-value">
                                            {group.createdBy?.username || 'Desconhecido'}
                                        </span>
                                    </div>
                                    <div className="adm-grp-info-item">
                                        <span className="adm-grp-info-label">CÓDIGO</span>
                                        <span className="adm-grp-info-value code">{group.inviteCode}</span>
                                    </div>
                                    <div className="adm-grp-info-item">
                                        <span className="adm-grp-info-label">MEMBROS</span>
                                        <span className="adm-grp-info-value">{group.memberCount}</span>
                                    </div>
                                    <div className="adm-grp-info-item">
                                        <span className="adm-grp-info-label">CRIADO EM</span>
                                        <span className="adm-grp-info-value">
                                            {new Date(group.createdAt).toLocaleDateString('pt-BR')}
                                        </span>
                                    </div>
                                </div>

                                <button
                                    className="adm-grp-btn-delete"
                                    onClick={() => onDeleteGroup(group._id, group.name)}
                                >
                                    <Trash2 size={16} />
                                    Deletar Grupo
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            {selectedGroup && (
                <GroupDetailsModal
                    isOpen={modalOpen}
                    groupId={selectedGroup._id}
                    groupName={selectedGroup.name}
                    groupIcon={selectedGroupIconUrl || undefined}
                    description={selectedGroup.description || undefined}
                    inviteCode={selectedGroup.inviteCode}
                    createdAt={selectedGroup.createdAt}
                    members={loadingMembers ? [] : members}
                    onClose={() => {
                        setModalOpen(false);
                        setSelectedGroup(null);
                        setSelectedGroupIconUrl(null);
                        setMembers([]);
                    }}
                />
            )}
        </div>
    );
};

export default AdminGroups;